import { Box, Button, CircularProgress, HStack, Stack, Text, VStack, useToast } from "@chakra-ui/react";
import { object, string } from "decoders";
import { useCallback, useMemo, useState } from "react";
import { AiFillPrinter } from "react-icons/ai";
import { useNavigate } from "react-router-dom";
import { P, match } from "ts-pattern";
import { useCreateDashboardPrintData, useDashboardData } from "../../services/projectService";
import { DashboardData, getDashboardStats } from "../../types/dashboardData";
import { matSitesFromDashboard } from "../../types/map";
import { ProjectID } from "../../types/project";
import { DashboardMapView } from "../DashboardMapView/DashboardMapView";
import { GenericDoubleBarGraph } from "../GenericDoubleBarGraph/GenericDoubleBarGraph";
import { NonDetectedAnalytes } from "../NonDetectedAnalytes/NonDetectedAnalytes";
import { PrintGraphsButton } from "../PrintGraphsButton/PrintGraphsButton";
import { ReferenceLimitsTable } from "../ReferenceLimitsTable/ReferenceLimitsTable";
import { SitesStatistics } from "../SitesStatistics/SitesStatistics";
import { SitesDescription } from "../SitesDescription/SitesDescription";
import { SiteData } from "../../types/site";

export function GenericDashboard({
  analyte,
  sites,
  startDate,
  endDate,
  project,
}: {
  analyte: string;
  sites: string[];
  startDate: Date;
  endDate: Date;
  project: ProjectID;
}) {
  const query = useDashboardData({ analyte, sites, startDate, endDate, project });
  const printMutation = usePrintMutation(project, analyte, sites, startDate, endDate);

  return match(query)
    .with({ isLoading: true }, () => <CircularProgress isIndeterminate color="brand.400" />)
    .with({ isError: true }, () => <Text color="red">Error loading dashboard data</Text>)
    .with({ data: P.nullish }, () => <></>)
    .otherwise(({ data }) => (
      <VStack alignItems={"stretch"} gap={8}>
        <HStack justifyContent={"space-between"}>
          <Button
            isLoading={printMutation.isLoading}
            onClick={() => printMutation.mutate()}
            leftIcon={<AiFillPrinter />}
          >
            Print Data
          </Button>
          <PrintGraphsButton />
        </HStack>
        <GenericDashboardPanels
          analyte={analyte}
          sitesData={data.sites}
          sites={sites}
          dashboardData={data}
          project={project}
          startDate={startDate}
          endDate={endDate}
        />
      </VStack>
    ));
}

function usePrintMutation(project: ProjectID, analyte: string, sites: string[], startDate: Date, endDate: Date) {
  const mutation = useCreateDashboardPrintData(project, analyte, sites, startDate, endDate);
  const navigate = useNavigate();
  const toast = useToast();

  const mutate = useCallback(
    () =>
      mutation.mutate(undefined, {
        onError: () => toast({ title: "An error ocurred, please try again later", status: "error" }),
        onSuccess: (data) => {
          const decoded = object({ printId: string }).value(data);

          if (decoded) {
            navigate(`/print/${decoded.printId}`);
          } else {
            toast({ title: "Failed to retreive print version", status: "error" });
          }
        },
      }),
    [mutation, navigate, toast],
  );

  return { ...mutation, mutate };
}

function GenericDashboardPanels({
  analyte,
  sitesData,
  sites,
  dashboardData,
  project,
  startDate,
  endDate,
}: {
  analyte: string;
  sitesData: SiteData[];
  sites: string[];
  dashboardData: DashboardData;
  project: ProjectID;
  startDate: Date;
  endDate: Date;
}) {
  const [showReferenceLine, setShowReferenceLine] = useState(true);
  const MapView = useMemo(
    () => <DashboardMapView mapSites={matSitesFromDashboard(dashboardData)} colorContext={project} />,
    [dashboardData, project],
  );

  return (
    <VStack alignItems={"stretch"} gap={8}>
      <Stack direction={{ base: "column", md: "row" }} gap={{ base: 8, md: 2 }}>
        <Box flex={1} minWidth={{ base: "100%", md: "600px" }}>
          {MapView}
        </Box>

        <Box flex={1}>
          <GenericDoubleBarGraph
            {...{
              dashboardData,
              sites,
              analyte,
              showReferenceLine,
              setShowReferenceLine,
              colorContext: project,
            }}
          />
        </Box>
      </Stack>

      {showReferenceLine && <ReferenceLimitsFootnotes dashboardData={dashboardData} />}

      <SitesDescription sites={sitesData} />
      <NonDetectedAnalytes startDate={startDate} endDate={endDate} sites={sites} project={project} />

      <Stack
        height={{ base: "auto", md: "350px" }}
        alignItems={{ md: "flex-start", base: "stretch" }}
        justifyContent={"center"}
        gap={8}
        direction={{ base: "column", md: "row" }}
      >
        {project !== "groundWater" && (
          <Box overflowY="auto" height={"100%"}>
            <ReferenceLimitsTable dashboardData={dashboardData} analyte={analyte} />
          </Box>
        )}
        <Box height={"100%"}>
          <SitesStatistics
            unit={dashboardData.unit}
            sitesStats={getDashboardStats(dashboardData, sites)}
            colorContext={project}
          />
        </Box>
      </Stack>
    </VStack>
  );
}

function ReferenceLimitsFootnotes({ dashboardData }: { dashboardData: DashboardData }) {
  const footnotes = Object.entries(dashboardData.referenceLimits)
    .map(([key, value]) => getReferenceLimitFootnote(key as keyof DashboardData["referenceLimits"], value))
    .filter((x) => x !== "")
    .map((footnote, index) => `${"*".repeat(index + 1)} ${footnote}`);

  return (
    <>
      {footnotes.map((footnote, index) => (
        <Text color="red" key={index}>
          {footnote}
        </Text>
      ))}
    </>
  );
}

function getReferenceLimitFootnote(key: keyof DashboardData["referenceLimits"], value: number | null): string {
  return match({ key, value })
    .with({ value: null }, () => "")
    .with(
      { key: "aquaticLife" },
      () =>
        "Aquatic Life reference limits are based on the Rincon Water Quality Standards Criterion Maximum Concentration (CMC) limits for surface water.",
    )

    .with(
      { key: "humanHealth" },
      () =>
        "Human Health reference limits are based on the Rincon Water Quality Standards ingestion of water and organism limits for surface water.",
    )

    .with(
      { key: "drinkingWater" },
      () =>
        "Drinking Water reference limits are based on U.S. Environmental Protection Agency Maximum Contaminant Levels (MCL) for drinking water.",
    )
    .exhaustive();
}
